import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Trophy, Star, Heart, Sparkles, Crown, Zap } from 'lucide-react';

interface QuizQuestion {
  id: number;
  question: string;
  options: string[];
  correct: string;
  category: 'colors' | 'numbers' | 'animals' | 'grammar';
  emoji: string;
  points: number;
}

const quizQuestions: QuizQuestion[] = [
  {
    id: 1,
    question: "What color is Betty's magical door?",
    options: ["Red", "Blue", "Green"],
    correct: "Blue",
    category: "colors",
    emoji: "🚪",
    points: 10
  },
  {
    id: 2,
    question: "How many butterflies does Betty count?",
    options: ["Three", "Four", "Five"],
    correct: "Five",
    category: "numbers",
    emoji: "🦋",
    points: 10
  },
  {
    id: 3,
    question: "What is the unicorn's name?",
    options: ["Luna", "Stella", "Bella"],
    correct: "Luna",
    category: "animals",
    emoji: "🦄",
    points: 15
  },
  {
    id: 4,
    question: "Betty _____ the magical door.",
    options: ["open", "opens", "opening"],
    correct: "opens",
    category: "grammar",
    emoji: "📝",
    points: 20
  },
  {
    id: 5,
    question: "What color are Spark's wings?",
    options: ["Orange", "Purple", "Yellow"],
    correct: "Orange",
    category: "colors",
    emoji: "🐉",
    points: 10
  },
  {
    id: 6,
    question: "How many magical gems did Spark lose?",
    options: ["Three", "Four", "Five"],
    correct: "Four",
    category: "numbers",
    emoji: "💎",
    points: 15
  },
  {
    id: 7,
    question: "The butterflies _____ in the garden.",
    options: ["dance", "dances", "dancing"],
    correct: "dance",
    category: "grammar",
    emoji: "💃",
    points: 20
  },
  {
    id: 8,
    question: "What color is Luna the unicorn?",
    options: ["Pink", "White", "Silver"],
    correct: "White",
    category: "colors",
    emoji: "🦄",
    points: 10
  }
];

export const CompetitiveQuiz: React.FC = () => {
  const [gameState, setGameState] = useState<'setup' | 'playing' | 'finished'>('setup');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [player1Name, setPlayer1Name] = useState('');
  const [player2Name, setPlayer2Name] = useState('');
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [player1Score, setPlayer1Score] = useState(0);
  const [player2Score, setPlayer2Score] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15);
  const [isTimerActive, setIsTimerActive] = useState(false);

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isTimerActive && timeLeft > 0) {
      interval = setInterval(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    } else if (timeLeft === 0) {
      handleTimeUp();
    }
    return () => clearInterval(interval);
  }, [isTimerActive, timeLeft]);

  const startGame = () => {
    if (player1Name.trim() && player2Name.trim()) {
      setGameState('playing');
      setIsTimerActive(true);
    }
  };

  const handleAnswer = () => {
    setIsTimerActive(false);
    setShowResult(true);
    
    const question = quizQuestions[currentQuestion];
    if (selectedAnswer === question.correct) {
      if (currentPlayer === 1) {
        setPlayer1Score(player1Score + question.points);
      } else {
        setPlayer2Score(player2Score + question.points);
      }
    }
  };

  const handleTimeUp = () => {
    setIsTimerActive(false);
    setShowResult(true);
    setSelectedAnswer(''); // No answer selected
  };

  const nextQuestion = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
      setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
      setSelectedAnswer('');
      setShowResult(false);
      setTimeLeft(15);
      setIsTimerActive(true);
    } else {
      setGameState('finished');
    }
  };

  const resetGame = () => {
    setGameState('setup');
    setCurrentQuestion(0);
    setCurrentPlayer(1);
    setPlayer1Score(0);
    setPlayer2Score(0);
    setSelectedAnswer('');
    setShowResult(false);
    setTimeLeft(15);
    setIsTimerActive(false);
    setPlayer1Name('');
    setPlayer2Name('');
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'colors': return 'bg-rainbow-100 text-rainbow-700 border-rainbow-300';
      case 'numbers': return 'bg-blue-100 text-blue-700 border-blue-300';
      case 'animals': return 'bg-green-100 text-green-700 border-green-300';
      case 'grammar': return 'bg-purple-100 text-purple-700 border-purple-300';
      default: return 'bg-gray-100 text-gray-700 border-gray-300';
    }
  };

  const getWinner = () => {
    if (player1Score > player2Score) return { name: player1Name, score: player1Score };
    if (player2Score > player1Score) return { name: player2Name, score: player2Score };
    return null; // Tie
  };

  if (gameState === 'setup') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50">
          <CardHeader>
            <CardTitle className="text-3xl text-center text-purple-600 flex items-center justify-center gap-2">
              <Trophy className="w-8 h-8" />
              Betty's Magic Quiz Challenge
              <Trophy className="w-8 h-8" />
            </CardTitle>
            <p className="text-center text-gray-600">Two players compete to answer questions about Betty's adventure!</p>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-purple-700">🌟 Player 1 Name:</label>
                <input
                  type="text"
                  value={player1Name}
                  onChange={(e) => setPlayer1Name(e.target.value)}
                  placeholder="Enter first player's name"
                  className="w-full p-3 border-2 border-purple-200 rounded-lg focus:border-purple-400 focus:outline-none"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-pink-700">💖 Player 2 Name:</label>
                <input
                  type="text"
                  value={player2Name}
                  onChange={(e) => setPlayer2Name(e.target.value)}
                  placeholder="Enter second player's name"
                  className="w-full p-3 border-2 border-pink-200 rounded-lg focus:border-pink-400 focus:outline-none"
                />
              </div>
            </div>

            <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-200">
              <h4 className="font-semibold text-yellow-800 mb-2">🎮 How to Play:</h4>
              <ul className="text-sm text-yellow-700 space-y-1">
                <li>• Players take turns answering questions</li>
                <li>• You have 15 seconds to answer each question</li>
                <li>• Correct answers earn points based on difficulty</li>
                <li>• The player with the most points wins!</li>
              </ul>
            </div>

            <div className="text-center">
              <Button
                onClick={startGame}
                disabled={!player1Name.trim() || !player2Name.trim()}
                className="bg-purple-500 hover:bg-purple-600 text-white px-8 py-3 text-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Start Magic Quiz!
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (gameState === 'finished') {
    const winner = getWinner();
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50">
          <CardHeader>
            <CardTitle className="text-3xl text-center text-orange-600 flex items-center justify-center gap-2">
              <Crown className="w-8 h-8" />
              Quiz Complete!
              <Crown className="w-8 h-8" />
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 text-center">
            <div className="text-6xl mb-4">
              {winner ? '🏆' : '🤝'}
            </div>
            
            {winner ? (
              <div>
                <h2 className="text-2xl font-bold text-green-600 mb-2">
                  🎉 {winner.name} Wins! 🎉
                </h2>
                <p className="text-lg text-gray-700">
                  Congratulations on your magical victory!
                </p>
              </div>
            ) : (
              <div>
                <h2 className="text-2xl font-bold text-blue-600 mb-2">
                  🤝 It's a Tie! 🤝
                </h2>
                <p className="text-lg text-gray-700">
                  Both players did amazingly well!
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-purple-50 rounded-lg border-2 border-purple-200">
                <h3 className="font-semibold text-purple-700">🌟 {player1Name}</h3>
                <p className="text-2xl font-bold text-purple-600">{player1Score} points</p>
              </div>
              <div className="p-4 bg-pink-50 rounded-lg border-2 border-pink-200">
                <h3 className="font-semibold text-pink-700">💖 {player2Name}</h3>
                <p className="text-2xl font-bold text-pink-600">{player2Score} points</p>
              </div>
            </div>

            <Button
              onClick={resetGame}
              className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 text-lg"
            >
              <Star className="w-5 h-5 mr-2" />
              Play Again!
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const question = quizQuestions[currentQuestion];
  const currentPlayerName = currentPlayer === 1 ? player1Name : player2Name;
  const currentPlayerColor = currentPlayer === 1 ? 'purple' : 'pink';

  return (
    <div className="max-w-4xl mx-auto p-6">
      <Card className={`bg-gradient-to-br ${currentPlayer === 1 ? 'from-purple-50 to-blue-50' : 'from-pink-50 to-red-50'}`}>
        <CardHeader>
          <div className="flex justify-between items-center mb-4">
            <div className="text-center">
              <Badge className="bg-purple-100 text-purple-700">🌟 {player1Name}</Badge>
              <p className="text-2xl font-bold text-purple-600">{player1Score}</p>
            </div>
            <div className="text-center">
              <Badge className="bg-gray-100 text-gray-700">
                Question {currentQuestion + 1}/{quizQuestions.length}
              </Badge>
            </div>
            <div className="text-center">
              <Badge className="bg-pink-100 text-pink-700">💖 {player2Name}</Badge>
              <p className="text-2xl font-bold text-pink-600">{player2Score}</p>
            </div>
          </div>
          
          <CardTitle className={`text-2xl text-center ${currentPlayer === 1 ? 'text-purple-600' : 'text-pink-600'} flex items-center justify-center gap-2`}>
            <Zap className="w-6 h-6" />
            {currentPlayerName}'s Turn!
            <Zap className="w-6 h-6" />
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Timer */}
          <div className="text-center">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full ${
              timeLeft <= 5 ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'
            }`}>
              <span className="text-lg font-bold">⏰ {timeLeft}s</span>
            </div>
          </div>

          {/* Question */}
          <div className="text-center">
            <div className="text-4xl mb-3">{question.emoji}</div>
            <Badge className={`mb-3 ${getCategoryColor(question.category)}`}>
              {question.category.toUpperCase()} • {question.points} points
            </Badge>
            <h3 className="text-xl font-semibold text-gray-800 mb-4">
              {question.question}
            </h3>
          </div>

          {/* Answer Options */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {question.options.map((option, index) => (
              <Button
                key={index}
                variant={selectedAnswer === option ? "default" : "outline"}
                size="lg"
                onClick={() => setSelectedAnswer(option)}
                disabled={showResult || timeLeft === 0}
                className={`h-16 text-lg ${
                  showResult 
                    ? option === question.correct 
                      ? 'bg-green-100 border-green-500 text-green-700' 
                      : option === selectedAnswer 
                        ? 'bg-red-100 border-red-500 text-red-700'
                        : ''
                    : selectedAnswer === option
                      ? `${currentPlayer === 1 ? 'bg-purple-500' : 'bg-pink-500'} text-white`
                      : 'bg-white hover:bg-gray-50'
                }`}
              >
                {option}
              </Button>
            ))}
          </div>

          {/* Result */}
          {showResult && (
            <div className={`p-4 rounded-lg border-2 ${
              selectedAnswer === question.correct 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="text-center">
                <div className="text-3xl mb-2">
                  {selectedAnswer === question.correct ? '🎉' : '😊'}
                </div>
                <p className={`font-semibold ${
                  selectedAnswer === question.correct ? 'text-green-700' : 'text-red-700'
                }`}>
                  {selectedAnswer === question.correct 
                    ? `Correct! +${question.points} points!` 
                    : timeLeft === 0 
                      ? 'Time\'s up! No points this time.'
                      : 'Not quite right! No points this time.'}
                </p>
                {selectedAnswer !== question.correct && (
                  <p className="text-sm text-gray-600 mt-1">
                    The correct answer was: <strong>{question.correct}</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Action Button */}
          <div className="text-center">
            {!showResult && timeLeft > 0 ? (
              <Button
                onClick={handleAnswer}
                disabled={!selectedAnswer}
                className={`${currentPlayer === 1 ? 'bg-purple-500 hover:bg-purple-600' : 'bg-pink-500 hover:bg-pink-600'} text-white px-8`}
              >
                Submit Answer
              </Button>
            ) : (
              <Button
                onClick={nextQuestion}
                className="bg-blue-500 hover:bg-blue-600 text-white px-8"
              >
                {currentQuestion < quizQuestions.length - 1 ? 'Next Question →' : 'See Results! 🏆'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};